import { Button, Toast, type ToastType, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  offices,
  companies,
  contacts,
  externalReferences,
  type Office,
  type Company,
  type Contact,
  type ExternalReference,
  type OfficeCountry,
  type NameTranslation,
} from "../../../api/addressBook.service";
import { CompanyPicker } from "../../../components/CompanyPicker/CompanyPicker";
import { DataTable } from "../../../components/DataTable/DataTable";
import { computeTranslationsDelta } from "../shared/translationsDelta";

type OfficeFormData = {
  companyId: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postcode: string;
  countryName: string;
  countryId: string;
  location: string;
  phone: string;
  fax: string;
  email: string;
  websiteUrl: string;
  isHeadOffice: boolean;
  translations: NameTranslation[];
};
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { TranslationsList } from "../../../components/TranslationsList/TranslationsList";
import styles from "../AddressBook.module.css";

type TabId = "details" | "contacts" | "ext-refs";

function OfficeDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const { hasAnyPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const isLive = hasFlag("address-book-live");
  const canWrite = hasAnyPermission(["addressbook.write"]) && isLive;

  const [office, setOffice] = useState<Office | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const [extRefs, setExtRefs] = useState<ExternalReference[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [countriesList, setCountriesList] = useState<OfficeCountry[]>([]);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [extRefsError, setExtRefsError] = useState<string | null>(null);
  const [resolvedCompanyName, setResolvedCompanyName] = useState<string | null>(null);

  const [officeContacts, setOfficeContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [hasMergeHistory, setHasMergeHistory] = useState(false);

  const [formData, setFormData] = useState<OfficeFormData>({
    companyId: "",
    name: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    county: "",
    postcode: "",
    countryName: "",
    countryId: "",
    location: "",
    phone: "",
    fax: "",
    email: "",
    websiteUrl: "",
    isHeadOffice: false,
    translations: [],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [translationsValid, setTranslationsValid] = useState(true);
  const [firmName, setFirmName] = useState<string | null>(null);
  const [firmTranslations, setFirmTranslations] = useState<NameTranslation[]>([]);

  const initialTranslationsRef = useRef<NameTranslation[]>([]);

  useEffect(() => {
    loadCountries();
    if (!isNew && id) {
      loadOffice(id);
      loadOfficeContacts(id);
      try {
        offices
          .getMergeHistory(id)
          .then(res => setHasMergeHistory(res.merges.length > 0))
          .catch(() => {
            /* merge history unavailable — hide button */
          });
      } catch {
        /* getMergeHistory unavailable */
      }
    }
  }, [id, isNew]);

  function handleCompanyChange(company: Company | null) {
    setSelectedCompany(company);
    handleInputChange("companyId", company?.id ?? "");
  }

  async function loadCountries() {
    try {
      setCountriesError(null);
      const countriesResponse = await offices.getCountries();
      const sorted = countriesResponse.slice().sort((a, b) => a.name.localeCompare(b.name));
      setCountriesList(sorted);
    } catch {
      setCountriesError("Failed to load countries. Please refresh the page.");
    }
  }

  async function loadOfficeContacts(officeId: string) {
    try {
      setContactsLoading(true);
      setContactsError(null);
      const response = await contacts.getAll({ officeId, limit: 100 });
      setOfficeContacts(response.data);
    } catch {
      setContactsError("Failed to load contacts.");
    } finally {
      setContactsLoading(false);
    }
  }

  async function loadOffice(officeId: string) {
    try {
      setLoading(true);
      setError(null);
      const response = await offices.getById(officeId);
      setOffice(response.data);

      // Fetch the firm to display its translations as "inherited" rows for
      // locales this office doesn't override (DEV-2591). Also resolves
      // companyName when missing (API-created offices).
      if (response.data.companyId) {
        try {
          const companyResponse = await companies.getById(response.data.companyId);
          if (!response.data.companyName) {
            setResolvedCompanyName(companyResponse.data.name);
          }
          setFirmName(companyResponse.data.name);
          setFirmTranslations(companyResponse.data.translations ?? []);
        } catch {
          setFirmName(null);
          setFirmTranslations([]);
        }
      } else {
        setFirmName(null);
        setFirmTranslations([]);
      }

      // Fetch external references using the office UUID
      try {
        setExtRefsError(null);
        const refsResponse = await externalReferences.getByEntity("office", response.data.id);
        setExtRefs(refsResponse.data);
      } catch {
        setExtRefsError("Failed to load external references.");
      }

      const loadedTranslations = response.data.translations ?? [];
      initialTranslationsRef.current = loadedTranslations;
      setFormData({
        companyId: String(response.data.companyId ?? ""),
        name: response.data.name,
        addressLine1: response.data.addressLine1 || "",
        addressLine2: response.data.addressLine2 || "",
        addressLine3: response.data.addressLine3 || "",
        city: response.data.city || "",
        county: response.data.county || "",
        postcode: response.data.postcode || "",
        countryName: response.data.countryName || "",
        countryId: response.data.countryId != null ? String(response.data.countryId) : "",
        location: response.data.location || "",
        phone: response.data.phone || "",
        fax: response.data.fax || "",
        email: response.data.email || "",
        websiteUrl: response.data.websiteUrl || "",
        isHeadOffice: response.data.isHeadOffice || false,
        translations: loadedTranslations,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load office"));
    } finally {
      setLoading(false);
    }
  }

  function handleInputChange(field: keyof OfficeFormData, value: string | boolean) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  function handleCountryChange(countryId: string) {
    const country = countriesList.find(c => String(c.id) === countryId);
    setFormData(prev => ({
      ...prev,
      countryId,
      countryName: country?.name || "",
    }));
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (isNew && !formData.companyId) {
      errors.companyId = "Company is required";
    }
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const currentTranslations = formData.translations;
      const basePayload = {
        name: formData.name.trim(),
        addressLine1: formData.addressLine1?.trim() || null,
        addressLine2: formData.addressLine2?.trim() || null,
        addressLine3: formData.addressLine3?.trim() || null,
        city: formData.city?.trim() || null,
        county: formData.county?.trim() || null,
        postcode: formData.postcode?.trim() || null,
        countryName: formData.countryName?.trim() || null,
        countryId: formData.countryId ? Number(formData.countryId) : null,
        location: formData.location?.trim() || null,
        phone: formData.phone?.trim() || null,
        fax: formData.fax?.trim() || null,
        email: formData.email?.trim() || null,
        websiteUrl: formData.websiteUrl?.trim() || null,
        isHeadOffice: formData.isHeadOffice ?? false,
      };

      if (isNew) {
        const { translations } = computeTranslationsDelta([], currentTranslations);
        const response = await offices.create(formData.companyId, {
          ...basePayload,
          translations: translations.length > 0 ? translations : undefined,
        });
        setToast({ message: "Office created successfully", type: "success" });
        navigate(ROUTES.addressBook.officeDetail(String(response.data.id)), { replace: true });
      } else if (id) {
        const { translations, removeTranslations } = computeTranslationsDelta(
          initialTranslationsRef.current,
          currentTranslations,
        );
        const response = await offices.update(id, {
          ...basePayload,
          companyId: formData.companyId,
          translations: translations.length > 0 ? translations : undefined,
          removeTranslations: removeTranslations.length > 0 ? removeTranslations : undefined,
        });
        setOffice(response.data);
        initialTranslationsRef.current = response.data.translations ?? currentTranslations;
        setFormData(prev => ({ ...prev, translations: initialTranslationsRef.current }));
        setToast({ message: "Office updated successfully", type: "success" });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save office";
      setToast({ message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!id || isNew) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${office?.name}"? This action cannot be undone.`,
    );
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      await offices.delete(id);
      setToast({ message: "Office deleted successfully", type: "success" });
      navigate(ROUTES.addressBook.offices);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete office";
      setToast({ message, type: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleBack() {
    navigate(ROUTES.addressBook.offices);
  }

  // Check for auth errors
  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  if (loading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader
          title={isNew ? "Create Office" : "Office Details"}
          subtitle={
            isNew ? "Add a new office to the address book" : "View and edit office information"
          }
        />
        <LoadingState message="Loading office..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Office Details" subtitle="View and edit office information" />
        <EmptyState
          variant="error"
          title="Failed to load office"
          message={error.message}
          action={{
            label: "Back to Offices",
            onClick: handleBack,
          }}
        />
      </div>
    );
  }

  function renderBreadcrumbs() {
    return (
      <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
        <Link to={ROUTES.addressBook.companies} className={styles.breadcrumbLink}>
          Companies
        </Link>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">
          /
        </span>
        {!isNew && office?.companyId ? (
          <>
            <Link
              to={ROUTES.addressBook.companyDetail(String(office.companyId))}
              className={styles.breadcrumbLink}
            >
              {office.companyName || resolvedCompanyName || "Company"}
            </Link>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <Link
              to={ROUTES.addressBook.companyDetail(String(office.companyId))}
              className={styles.breadcrumbLink}
            >
              Offices
            </Link>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <span className={styles.breadcrumbCurrent}>{office.name}</span>
          </>
        ) : (
          <>
            <Link to={ROUTES.addressBook.offices} className={styles.breadcrumbLink}>
              Offices
            </Link>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <span className={styles.breadcrumbCurrent}>New Office</span>
          </>
        )}
      </nav>
    );
  }

  function renderTabs() {
    if (isNew) return null;

    const tabs: { id: TabId; label: string; count?: number }[] = [
      { id: "details", label: "Details" },
      { id: "contacts", label: "Contacts", count: officeContacts.length },
      { id: "ext-refs", label: "Ext. Refs" },
    ];

    return (
      <div className={styles.tabs} role="tablist" aria-label="Office sections">
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : styles.tabBadgeInactive}`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  function computeInheritedTranslations() {
    if (isNew || firmTranslations.length === 0) return [];
    const ownLocales = new Set(formData.translations.map(t => t.localeCode.trim()).filter(Boolean));
    return firmTranslations
      .filter(t => !ownLocales.has(t.localeCode.trim()))
      .map(t => ({ ...t, source: firmName || "firm" }));
  }

  function renderDetailsTab() {
    return (
      <form onSubmit={handleSubmit} className={styles.formContainer}>
        {isNew && (
          <div className={styles.formField} style={{ marginBottom: "1.5rem" }}>
            <CompanyPicker
              label="Company *"
              value={selectedCompany}
              onChange={handleCompanyChange}
              error={formErrors.companyId}
              disabled={!canWrite}
            />
          </div>
        )}

        {/* Row 1: Name + Head Office checkbox */}
        <div className={`${styles.formRow} ${styles.formRowNameHead}`}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="name">
              Name *
            </label>
            <input
              id="name"
              type="text"
              className={styles.formInput}
              value={formData.name}
              onChange={e => handleInputChange("name", e.target.value)}
              placeholder="Enter office name"
              disabled={!canWrite}
            />
            {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
          </div>
          <div className={styles.checkboxField}>
            <input
              id="isHeadOffice"
              type="checkbox"
              checked={formData.isHeadOffice}
              onChange={e => handleInputChange("isHeadOffice", e.target.checked)}
              disabled={!canWrite}
            />
            <label htmlFor="isHeadOffice">Head Office</label>
          </div>
        </div>

        {/* Row 2: Address Line 1 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine1">
              Address Line 1
            </label>
            <input
              id="addressLine1"
              type="text"
              className={styles.formInput}
              value={formData.addressLine1 || ""}
              onChange={e => handleInputChange("addressLine1", e.target.value)}
              placeholder="Enter street address"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 3: Address Line 2 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine2">
              Address Line 2
            </label>
            <input
              id="addressLine2"
              type="text"
              className={styles.formInput}
              value={formData.addressLine2 || ""}
              onChange={e => handleInputChange("addressLine2", e.target.value)}
              placeholder="Enter address line 2"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 4: Address Line 3 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine3">
              Address Line 3
            </label>
            <input
              id="addressLine3"
              type="text"
              className={styles.formInput}
              value={formData.addressLine3 || ""}
              onChange={e => handleInputChange("addressLine3", e.target.value)}
              placeholder="Enter address line 3"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 5: City, County, Postcode */}
        <div className={`${styles.formRow} ${styles.formRow3Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="city">
              City
            </label>
            <input
              id="city"
              type="text"
              className={styles.formInput}
              value={formData.city || ""}
              onChange={e => handleInputChange("city", e.target.value)}
              placeholder="Enter city"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="county">
              County
            </label>
            <input
              id="county"
              type="text"
              className={styles.formInput}
              value={formData.county || ""}
              onChange={e => handleInputChange("county", e.target.value)}
              placeholder="Enter county"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="postcode">
              Postcode
            </label>
            <input
              id="postcode"
              type="text"
              className={styles.formInput}
              value={formData.postcode || ""}
              onChange={e => handleInputChange("postcode", e.target.value)}
              placeholder="Enter postcode"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 6: Location */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="location">
              Location
            </label>
            <input
              id="location"
              type="text"
              className={styles.formInput}
              value={formData.location || ""}
              onChange={e => handleInputChange("location", e.target.value)}
              placeholder="Enter location"
              disabled={!canWrite}
            />
            <span className={styles.helperText}>
              Free-text descriptor e.g. &apos;City of London&apos;
            </span>
          </div>
        </div>

        {/* Row 7: Country */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="countryId">
              Country
            </label>
            <select
              id="countryId"
              className={styles.formInput}
              value={formData.countryId}
              onChange={e => handleCountryChange(e.target.value)}
              disabled={!canWrite}
            >
              <option value="">Select a country</option>
              {countriesList.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            {countriesError && <span className={styles.errorText}>{countriesError}</span>}
          </div>
        </div>

        {/* Row 8: Phone, Fax */}
        <div className={`${styles.formRow} ${styles.formRow2Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="phone">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              className={styles.formInput}
              value={formData.phone || ""}
              onChange={e => handleInputChange("phone", e.target.value)}
              placeholder="+1 234 567 8900"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="fax">
              Fax
            </label>
            <input
              id="fax"
              type="tel"
              className={styles.formInput}
              value={formData.fax || ""}
              onChange={e => handleInputChange("fax", e.target.value)}
              placeholder="Enter fax number"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 9: Email, Website URL */}
        <div className={`${styles.formRow} ${styles.formRow2Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={styles.formInput}
              value={formData.email || ""}
              onChange={e => handleInputChange("email", e.target.value)}
              placeholder="Enter email address"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="websiteUrl">
              Website URL
            </label>
            <input
              id="websiteUrl"
              type="url"
              className={styles.formInput}
              value={formData.websiteUrl || ""}
              onChange={e => handleInputChange("websiteUrl", e.target.value)}
              placeholder="https://example.com"
              disabled={!canWrite}
            />
          </div>
        </div>

        <div
          className={`${styles.formField} ${styles.formFieldFull}`}
          style={{ marginTop: "1.5rem" }}
        >
          <label className={styles.formLabel}>
            Translations
            <span className={styles.helperText} style={{ marginLeft: "0.5rem" }}>
              (optional) Localised names for this office
            </span>
          </label>
          <TranslationsList
            translations={formData.translations}
            onChange={translations => setFormData(prev => ({ ...prev, translations }))}
            disabled={!canWrite}
            onValidityChange={setTranslationsValid}
            inheritedTranslations={computeInheritedTranslations()}
          />
        </div>

        <div className={styles.formActions}>
          {isNew ? (
            <>
              <Button
                variant="outlined"
                color="default"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {isLive && (
                <PermissionGate permission="addressbook.write">
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting || !translationsValid}
                  >
                    {isSubmitting ? "Creating..." : "Create Office"}
                  </Button>
                </PermissionGate>
              )}
            </>
          ) : (
            <>
              {isLive && (
                <PermissionGate permission="addressbook.delete">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </PermissionGate>
              )}
              {isLive && (
                <PermissionGate permission="addressbook.write">
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting || !translationsValid}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </PermissionGate>
              )}
            </>
          )}
        </div>
      </form>
    );
  }

  function renderContactsTab() {
    const contactColumns: Column<Contact>[] = [
      {
        header: "Name",
        accessor: "name",
        render: (_value: unknown, row: Contact) => (
          <span
            style={{ fontWeight: 600, cursor: "pointer", color: "var(--color-primary)" }}
            onClick={() => navigate(ROUTES.addressBook.contactDetail(String(row.id)))}
          >
            {row.name}
          </span>
        ),
      },
      {
        header: "Job Title",
        accessor: "jobTitle",
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Email",
        accessor: "email",
        render: (value: unknown) => (value as string) || "-",
      },
    ];

    return (
      <div className={styles.section}>
        {contactsLoading ? (
          <LoadingState message="Loading contacts..." />
        ) : contactsError ? (
          <span className={styles.errorText}>{contactsError}</span>
        ) : (
          <DataTable
            data={officeContacts}
            columns={contactColumns}
            onRowClick={contact => navigate(ROUTES.addressBook.contactDetail(String(contact.id)))}
            rowKey="id"
            emptyMessage="No contacts associated with this office."
          />
        )}
      </div>
    );
  }

  function formatRefLabel(type: string): string {
    return type
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  function renderExtRefsTab() {
    if (!office) return null;

    // External references from the API
    const apiRefs: { label: string; value: string }[] = extRefs.map(ref => ({
      label: formatRefLabel(ref.referenceType),
      value: ref.referenceValue,
    }));

    // Static identifiers from the office object
    const staticRefs: { label: string; value: string | null | undefined }[] = [
      { label: "ID", value: office.id },
      { label: "Office ID", value: String(office.officeId) },
      { label: "Company ID", value: office.companyId },
      { label: "Firm Reference", value: office.firmRef },
      { label: "Office Reference", value: office.officeRef },
      { label: "CRM Entity Number", value: office.crmEntityNumber },
      { label: "Slug", value: office.slug },
      { label: "Country Term Slug", value: office.countryTermSlug },
      { label: "Country Slug", value: office.countrySlug },
      { label: "Country Parent Name", value: office.countryParentName },
      { label: "Country Parent Slug", value: office.countryParentSlug },
      { label: "Company Slug", value: office.companySlug },
      {
        label: "Created",
        value: office.createdAt ? new Date(office.createdAt).toLocaleString() : null,
      },
      {
        label: "Updated",
        value: office.updatedAt ? new Date(office.updatedAt).toLocaleString() : null,
      },
    ];

    const visibleStaticRefs = staticRefs.filter(r => r.value != null && r.value !== "");

    return (
      <div className={styles.section}>
        {extRefsError && <span className={styles.errorText}>{extRefsError}</span>}
        {apiRefs.length > 0 && (
          <>
            <h2 className={styles.sectionTitle}>External References</h2>
            <div className={styles.infoGrid}>
              {apiRefs.map(ref => (
                <div key={ref.label} className={styles.infoItem}>
                  <span className={styles.infoLabel}>{ref.label}</span>
                  <span className={styles.infoValue}>{ref.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <h2 className={styles.sectionTitle}>Identifiers</h2>
        <div className={styles.infoGrid}>
          {visibleStaticRefs.map(ref => (
            <div key={ref.label} className={styles.infoItem}>
              <span className={styles.infoLabel}>{ref.label}</span>
              <span className={styles.infoValue}>{ref.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderTabContent() {
    switch (activeTab) {
      case "details":
        return renderDetailsTab();
      case "contacts":
        return renderContactsTab();
      case "ext-refs":
        return renderExtRefsTab();
      default:
        return renderDetailsTab();
    }
  }

  return (
    <div className={styles.detailWrapper}>
      <div>
        <PageHeader
          title={isNew ? "Create Office" : office?.name || "Office Details"}
          subtitle={
            isNew ? "Add a new office to the address book" : "View and edit office information"
          }
          meta={
            !isNew && office ? (
              <div className={styles.metaStrip}>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>WP Term ID</span>
                  <span className={styles.metaValue}>{office.officeId ?? "—"}</span>
                </span>
                {office.crmEntityNumber && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>CRM</span>
                    <span className={styles.metaValue}>{office.crmEntityNumber}</span>
                  </span>
                )}
                {office.firmRef && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>Firm Ref</span>
                    <span className={styles.metaValue}>{office.firmRef}</span>
                  </span>
                )}
                {office.officeRef && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>Office Ref</span>
                    <span className={styles.metaValue}>{office.officeRef}</span>
                  </span>
                )}
              </div>
            ) : undefined
          }
        />
      </div>

      {renderBreadcrumbs()}

      {!isNew && canWrite && (
        <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
          {activeTab === "contacts" && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(ROUTES.addressBook.officeContactNew(id!))}
            >
              Create Contact
            </Button>
          )}
          <Button
            variant="outlined"
            color="default"
            onClick={() => navigate(ROUTES.addressBook.officeMerge(id!))}
          >
            Merge into another office
          </Button>
          <Button
            variant="outlined"
            color="default"
            disabled={!hasMergeHistory}
            onClick={() => navigate(ROUTES.addressBook.officeDemerge(id!))}
          >
            Undo Merge
          </Button>
        </div>
      )}

      {renderTabs()}

      {isNew ? renderDetailsTab() : renderTabContent()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default OfficeDetail;
